import React, { Fragment, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Intent } from "@blueprintjs/core";
import { Tabs } from "components/navigation";
import { ShowHide } from "components/layout";
import { NotificationInline, NotificationToaster } from "components/notifications";
import { EarnedRecognitionService } from "services";
import { DriverHoursKpiSummary } from "./DriverHoursKpiSummary";
import { MaintenanceKpiSummary } from "./MaintenanceKpiSummary";

// Semantic tab keys, persisted to Redux so the selection survives the KPI drilldown round-trip. These
// are NOT positional — a stream can be hidden, so the visible Tabs index is mapped back to these keys.
const driversStreamKey = 0;
const maintenanceStreamKey = 1;

// Placeholder tab bar rendered as a skeleton while the enrolment flags load — the skeleton class
// hides the labels, so it shows a loading affordance without revealing which streams the account has.
const loadingSkeletonTabs = [{ title: "Drivers" }, { title: "Maintenance" }];

function noOp() {
}

export function EarnedRecognition() {
    const dispatch = useDispatch();
    const activeTab = useSelector(state => state.earnedRecognition.activeTab);

    // Enrolment flags decide which streams (and so which tabs) the account can see. Until they resolve
    // the page shows neither the tabs nor the not-signed-up message.
    const [loading, setLoading] = useState(true);
    const [sendDriverData, setSendDriverData] = useState(false);
    const [sendMaintenanceData, setSendMaintenanceData] = useState(false);
    // A load error must not be shown as "not enrolled" — a transient failure to fetch the enrolment
    // flags is a different situation from an account that is genuinely not signed up. When this is set
    // the not-signed-up message is suppressed and the danger toast is the only affordance shown.
    const [hasError, setHasError] = useState(false);

    useEffect(loadEnrolment, []);

    function loadEnrolment() {
        EarnedRecognitionService.getEarnedRecognition().then(
            (settings) => {
                const driverEnabled = settings.sendDriverData ?? false;
                const maintenanceEnabled = settings.sendMaintenanceData ?? false;

                setSendDriverData(driverEnabled);
                setSendMaintenanceData(maintenanceEnabled);
                reconcileActiveTab(driverEnabled, maintenanceEnabled);

                setLoading(false);
            },
            () => {
                NotificationToaster.show(Intent.DANGER, "Could not load Earned Recognition settings. Please refresh the page.");
                setHasError(true);
                setLoading(false);
            }
        );
    }

    // The persisted tab may point at a stream this account is not enrolled in (e.g. it kept Maintenance
    // from a previous account but only driver data is enabled here). Snap it to the first enabled stream
    // so a hidden stream is never the active one — this also selects the sole stream when only one is on.
    function reconcileActiveTab(driverEnabled, maintenanceEnabled) {
        const enabledKeys = [];
        if (driverEnabled) {
            enabledKeys.push(driversStreamKey);
        }
        if (maintenanceEnabled) {
            enabledKeys.push(maintenanceStreamKey);
        }

        if (enabledKeys.length > 0 && !enabledKeys.includes(activeTab)) {
            dispatch({ type: "SET_EARNED_RECOGNITION_TAB", payload: enabledKeys[0] });
        }
    }

    // Only the enrolled streams are offered as tabs, each carrying its semantic key.
    const streams = [];
    if (sendDriverData) {
        streams.push({ key: driversStreamKey, title: "Drivers" });
    }
    if (sendMaintenanceData) {
        streams.push({ key: maintenanceStreamKey, title: "Maintenance" });
    }

    const tabs = streams.map((stream) => ({ title: stream.title, selected: activeTab === stream.key }));

    function onTabClick(positionalIndex) {
        // Tabs reports the index into the visible tabs; translate it back to the stream's semantic key.
        dispatch({ type: "SET_EARNED_RECOGNITION_TAB", payload: streams[positionalIndex].key });
    }

    return (
        <Fragment>
            <h1>Earned Recognition</h1>
            <ShowHide
                evaluator={!loading}
                show={(
                    <ShowHide
                        evaluator={streams.length > 0}
                        show={(
                            <Fragment>
                                <Tabs tabs={tabs} onClick={onTabClick} />
                                <div className="spacer-bottom"></div>
                                <ShowHide evaluator={activeTab === driversStreamKey} show={(<DriverHoursKpiSummary />)} />
                                <ShowHide evaluator={activeTab === maintenanceStreamKey} show={(<MaintenanceKpiSummary />)} />
                            </Fragment>
                        )}
                        hide={(
                            <ShowHide
                                evaluator={!hasError}
                                show={(
                                    <NotificationInline
                                        intent="warning"
                                        allowClose={false}
                                        show={true}
                                        text="This account is not signed up for Earned Recognition."
                                    />
                                )}
                            />
                        )}
                    />
                )}
                hide={(
                    // Loading skeleton: the real enrolment-dependent tabs and the not-signed-up message
                    // only appear once the flags resolve, so show a skeleton tab bar in the meantime.
                    <Tabs loading={true} tabs={loadingSkeletonTabs} onClick={noOp} />
                )}
            />
        </Fragment>
    );
}
